import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { PortalPage, Pill } from "../../components/portal/PortalPage";
import { fmtDate, JOB_STATUS, EMERGENCY_STATUS, REQUEST_STATUS } from "../../components/portal/portalUtils";
import { CaretLeft, CaretRight, Briefcase, Lightning, ClipboardText } from "@phosphor-icons/react";
import { FamilyFilter, ForClientChip, useFamilyFilter } from "../../components/portal/FamilyFilter";

const KIND = {
  job: { icon: Briefcase, cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-300", path: "/portal/jobs", map: JOB_STATUS },
  visit: { icon: Briefcase, cls: "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 border-teal-300", path: "/portal/jobs", map: JOB_STATUS },
  emergency: { icon: Lightning, cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border-red-300", path: "/portal/emergencies", map: EMERGENCY_STATUS },
  request: { icon: ClipboardText, cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300", path: "/portal/requests", map: REQUEST_STATUS },
};
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export default function ClientCalendarPage() {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(iso(new Date()));

  const range = useMemo(() => {
    const start = new Date(cursor); start.setDate(1 - ((start.getDay() + 6) % 7));
    const end = new Date(start); end.setDate(start.getDate() + 41);
    return { start: iso(start), end: iso(end), startDate: start };
  }, [cursor]);

  const fam = useFamilyFilter();
  useEffect(() => {
    API.get(`/client-hub/calendar?start=${range.start}&end=${range.end}${fam.selected ? `&client_focus=${fam.selected}` : ""}`).then(({ data }) => setEvents(data.events || [])).catch(() => setEvents([]));
  }, [range, fam.selected]);

  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [events]);

  const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(range.startDate); d.setDate(d.getDate() + i); return d; });
  const today = iso(new Date());
  const dayEvents = byDay[selected] || [];

  return (
    <PortalPage title="Calendar" subtitle="Scheduled visits, emergencies and target dates across your properties." testid="client-calendar" actions={<FamilyFilter fam={fam} testid="calendar-client-filter" />}>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border rounded-lg bg-card p-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="icon" onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))} data-testid="cal-prev"><CaretLeft size={16} /></Button>
            <h2 className="text-base font-semibold font-[Chivo]" data-testid="cal-month">{cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</h2>
            <Button variant="ghost" size="icon" onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))} data-testid="cal-next"><CaretRight size={16} /></Button>
          </div>
          <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-1 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const k = iso(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const evs = byDay[k] || [];
              return (
                <button key={k} onClick={() => setSelected(k)} className={`min-h-[72px] rounded-md border p-1 text-left transition-colors ${selected === k ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"} ${inMonth ? "" : "opacity-40"}`} data-testid={`cal-day-${k}`}>
                  <span className={`text-[11px] font-medium inline-flex h-5 w-5 items-center justify-center rounded-full ${k === today ? "bg-primary text-primary-foreground" : ""}`}>{d.getDate()}</span>
                  <div className="mt-0.5 space-y-0.5">
                    {evs.slice(0, 2).map((e) => (
                      <div key={`${e.kind}-${e.id}`} className={`text-[9px] leading-tight px-1 rounded truncate border-l-2 ${KIND[e.kind].cls}`}>{e.start_time ? `${e.start_time} ` : ""}{e.title}</div>
                    ))}
                    {evs.length > 2 && <div className="text-[9px] text-muted-foreground px-1">+{evs.length - 2} more</div>}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-blue-400" /> Job visit</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-red-400" /> Emergency</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-400" /> Request target date</span>
          </div>
        </div>

        <div className="border border-border rounded-lg bg-card p-4" data-testid="cal-day-panel">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3">{fmtDate(selected, { weekday: "long", day: "numeric", month: "long" })}</h3>
          {dayEvents.length === 0 ? <p className="text-xs text-muted-foreground">Nothing scheduled this day.</p> : (
            <ul className="space-y-2">
              {dayEvents.map((e) => {
                const k = KIND[e.kind]; const Icon = k.icon;
                return (
                  <li key={`${e.kind}-${e.id}`}>
                    <Link to={`${k.path}/${e.id}`} className="flex gap-3 border border-border rounded-md p-3 hover:border-primary/40" data-testid={`cal-event-${e.id}`}>
                      <Icon size={18} className="shrink-0 mt-0.5 text-muted-foreground" weight="duotone" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold truncate">{e.title}</p>
                        {e.for_client && <p className="mb-0.5"><ForClientChip name={e.for_client} clientId={e.client_id} /></p>}
                        <p className="text-[10px] text-muted-foreground">{e.reference}{e.start_time ? ` · ${e.start_time}${e.end_time ? `–${e.end_time}` : ""}` : ""}{e.supplier ? ` · ${e.supplier}` : ""}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{e.address}</p>
                      </div>
                      <Pill map={k.map} value={e.status} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </PortalPage>
  );
}
